import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../lib/auth";

/**
 * Notification Bell — surfaces active announcements + the caller's own
 * broadcast log. Unread count is derived from `announcements/active`.
 */
export default function NotificationBell() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const ref = useRef(null);
  const nav = useNavigate();

  const reload = () => {
    if (!user) return;
    Promise.all([
      api.get("/announcements/active").then((r) => r.data || []).catch(() => []),
      // Iter 63.4 — pull personal notifications too so admins see payment
      // alerts + all other events (not just broadcast announcements).
      api.get("/notifications?unread_only=true&limit=30")
        .then((r) => (r.data || []).map((n) => ({
          id: n.id,
          title: n.title,
          body: n.body,
          cta_url: n.link,
          cta_label: "View",
          read: !!n.read,
          _kind: "personal",
        })))
        .catch(() => []),
    ]).then(([ann, personal]) => {
      // Personal notifications first (they're user-specific), then announcements.
      setItems([...personal, ...ann]);
    });
  };

  useEffect(() => {
    reload();
    const t = setInterval(reload, 60_000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  // Click outside to close
  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    if (open) document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const unread = items.filter(i => !i.read).length;

  const handleClick = async (item) => {
    setOpen(false);
    if (!item.read) {
      if (item._kind === "personal") {
        await api.post(`/notifications/${item.id}/read`).catch(() => {});
      } else {
        await api.post(`/announcements/${item.id}/read`).catch(() => {});
      }
      reload();
    }

    const rawTarget = item?.cta_url || item?.link || item?.url || "";
    const role = user?.role;

    let target = (rawTarget || "").trim();
    if (!target) return;

    // Legacy /dashboard/bookings/:id links still exist in older rows. Convert
    // them to the canonical dashboard URL with the highlight fragment so the
    // correct tab opens and the matching booking row flashes.
    const legacyBookingMatch = /^\/dashboard\/bookings\/([\w-]+)/.exec(target);
    if (legacyBookingMatch) {
      const bid = legacyBookingMatch[1];
      if (role === "artist")       target = `/artist?tab=bookings&highlight=${bid}`;
      else if (role === "agency")  target = `/agency/bookings?highlight=${bid}`;
      else if (role === "admin")   target = `/admin?tab=bookings&highlight=${bid}`;
      else                          target = `/customer?tab=bookings&highlight=${bid}`;
    }

    if (target.startsWith("http")) {
      window.open(target, "_blank", "noopener,noreferrer");
      return;
    }

    if (target.startsWith("/")) {
      nav(target);
      return;
    }

    if (target.startsWith("#")) {
      nav({ hash: target });
    }
  };

  if (!user) return null;

  return (
    <div ref={ref} style={{ position: "relative" }} data-testid="notification-bell">
      <button
        className="btn btn-ghost btn-sm"
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => setOpen((prev) => !prev)}
        aria-label="Notifications"
        data-testid="notification-bell-btn"
        style={{ position: "relative", padding: "var(--space-1) 10px" }}
      >
        <Bell size={18} strokeWidth={1.8} aria-hidden="true" />
        {unread > 0 && (
          <span data-testid="notification-badge" style={{
            position: "absolute", top: -2, right: -2,
            background: "#dc2626", color: "#fff",
            borderRadius: "10px", padding: "var(--space-1) 6px",
            fontSize: 10, fontWeight: 700, minWidth: 18, textAlign: "center",
          }}>{unread}</span>
        )}
      </button>
      {open && (
        <div
          className="notif-dropdown"
          style={{
            position: "absolute", top: "calc(100% + 8px)", right: 0,
            width: 340, maxHeight: 460, overflowY: "auto",
            background: "#fff",
            border: "1px solid var(--glass-border)", borderRadius: 12,
            boxShadow: "0 20px 60px rgba(37,31,20,0.18)", padding: "var(--space-2)", zIndex: 100,
          }}
          data-testid="notification-dropdown"
        >
          <div style={{
            padding: "var(--space-2) 12px", borderBottom: "1px solid var(--glass-border)",
            display: "flex", alignItems: "center", justifyContent: "space-between",
          }}>
            <span style={{ fontFamily: "var(--font-serif)", fontWeight: 700 }}>Notifications</span>
            {unread > 0 && (
              <button
                onClick={async () => {
                  await api.post("/notifications/read-all").catch(() => {});
                  reload();
                }}
                data-testid="notification-mark-all-read"
                style={{
                  background: "transparent", border: "1px solid var(--glass-border)",
                  color: "var(--gold-light)", padding: "var(--space-1) 10px", borderRadius: 999,
                  fontSize: 11, cursor: "pointer",
                }}
              >
                Mark all read
              </button>
            )}
          </div>
          {items.length === 0 ? (
            <div style={{ padding: "var(--space-6)", textAlign: "center", color: "var(--white-muted)", fontSize: 13 }}>
              You're all caught up.
            </div>
          ) : items.map((it) => (
            <button key={it.id}
              onClick={() => handleClick(it)}
              data-testid={`notification-item-${it.id}`}
              style={{
                display: "block", width: "100%", textAlign: "left",
                padding: "var(--space-2) 12px", border: "none", cursor: "pointer",
                background: it.read ? "transparent" : "rgba(212,175,55,0.08)",
                borderRadius: 8, marginBottom: "var(--space-1)", color: "var(--white)",
              }}
            >
              <div style={{ fontWeight: 600, fontSize: 13.5, display: "flex", alignItems: "center", gap: "var(--space-1)" }}>
                {!it.read && <span style={{ width: 6, height: 6, borderRadius: "50%", background: "var(--gold)" }} />}
                {it.title}
              </div>
              {it.body && <div style={{ color: "var(--white-muted)", fontSize: 12, marginTop: "var(--space-1)" }}>{it.body}</div>}
              {it.cta_url && <div style={{ color: "var(--gold-light)", fontSize: 12, marginTop: "var(--space-1)" }}>{it.cta_label || "Learn more"} →</div>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
